import React, { useState, useEffect, useContext } from 'react';
import DashboardLayout from '../components/layout/DashboardLayout';
import { AuthContext, API } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { BarChart3, DollarSign, MessageSquare, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import TrainingCalendar from '../components/TrainingCalendar';

const FranchiseeDashboard = () => {
  const { token, user } = useContext(AuthContext);
  const [stats, setStats] = useState(null);
  const [franchisee, setFranchisee] = useState(null);
  const [trainingEvents, setTrainingEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [token]);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, franchiseeRes, trainingRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`, { headers: { Authorization: `Bearer ${token}` } }),
        user.franchisee_id ? axios.get(`${API}/franchisees/${user.franchisee_id}`, { headers: { Authorization: `Bearer ${token}` } }) : null,
        axios.get(`${API}/training/events`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setStats(statsRes.data);
      if (franchiseeRes) setFranchisee(franchiseeRes.data);
      setTrainingEvents(trainingRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="franchisee-dashboard">
        {/* Welcome Section */}
        <div className="bg-gradient-to-r from-primary to-primary-800 rounded-xl p-8 text-white">
          <h1 className="text-3xl font-heading font-bold mb-2">Welcome, {user?.full_name}</h1>
          <p className="text-primary-100">{franchisee?.company_name || 'Your Franchise Dashboard'}</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="border border-border/60 rounded-xl shadow-sm" data-testid="total-kpis-stat">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total KPIs Submitted</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{stats?.total_kpis || 0}</p>
                </div>
                <div className="bg-primary h-12 w-12 rounded-lg flex items-center justify-center text-white">
                  <BarChart3 className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/60 rounded-xl shadow-sm" data-testid="total-fees-stat">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Fee Submissions</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{stats?.total_fees || 0}</p>
                </div>
                <div className="bg-secondary h-12 w-12 rounded-lg flex items-center justify-center text-white">
                  <DollarSign className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-border/60 rounded-xl shadow-sm" data-testid="total-complaints-stat">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground font-medium">Total Complaints</p>
                  <p className="text-3xl font-heading font-bold mt-2 tabular-nums">{stats?.total_complaints || 0}</p>
                </div>
                <div className="bg-chart-3 h-12 w-12 rounded-lg flex items-center justify-center text-white">
                  <MessageSquare className="h-6 w-6" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Kindergarten Details */}
        {franchisee && (
          <Card className="border border-border/60 rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Kindergarten Details</CardTitle>
              <CardDescription>Your registered kindergarten information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Name</p>
                    <p className="font-medium">{franchisee.kindergarten.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Location</p>
                    <p className="font-medium">{franchisee.kindergarten.location}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Contact Person</p>
                    <p className="font-medium">{franchisee.kindergarten.contact_person}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Capacity</p>
                    <p className="font-medium tabular-nums">{franchisee.kindergarten.capacity} students</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current Enrollment</p>
                    <p className="font-medium tabular-nums">{franchisee.kindergarten.current_enrollment} students</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Staff Count</p>
                    <p className="font-medium tabular-nums">{franchisee.kindergarten.staff_count} staff</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Recent KPIs Chart */}
        {stats?.recent_kpis && stats.recent_kpis.length > 0 && (
          <Card className="border border-border/60 rounded-xl shadow-sm">
            <CardHeader>
              <CardTitle className="font-heading">Recent KPI Performance</CardTitle>
              <CardDescription>Your performance scores over time</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stats.recent_kpis}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="overall_score" fill="#6B46C1" name="Overall Score" />
                  <Bar dataKey="attendance_rate" fill="#F59E0B" name="Attendance Rate" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Training Calendar Section */}
        <Card className="border border-border/60 rounded-xl shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading">Upcoming Training & Events</CardTitle>
            <CardDescription>Training sessions and events calendar</CardDescription>
          </CardHeader>
          <CardContent>
            {trainingEvents.length > 0 ? (
              <TrainingCalendar events={trainingEvents} />
            ) : (
              <div className="text-center py-12 text-muted-foreground">
                No training events scheduled yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FranchiseeDashboard;